"use client"

import { ApertureStyles, T, FM, MAXW, SectionEyebrow, RevealGrid, ArrowR } from "./_shared"

export default function ApertureValidators() {
  // Each subhead leads with the BENEFIT (what it saves you / protects),
  // not the mechanism. The tech detail stays as a second-line proof
  // point so engineers don't dismiss it as marketing fluff. Power
  // verbs first — "catches", "blocks", "rescues", "protects".
  const items: { t: string; d: string; col: string; tag: string }[] = [
    { t: "Syntax",          d: "Catches malformed addresses before they ever leave your ESP: missing @, broken domains, illegal characters, and other obvious hard-bounce cases.",       col: "lg:col-span-6", tag: "01" },
    { t: "Live MX",         d: "Checks whether the domain can receive mail. Typo'd, parked, or expired domains get separated from addresses that are actually sendable.",                col: "lg:col-span-6", tag: "02" },
    { t: "Duplicates",      d: "Keeps the first occurrence and flags repeat rows so your export does not inflate sends, costs, or engagement metrics.",                                    col: "lg:col-span-4", tag: "03" },
    { t: "Role-based",      d: "Flags generic inboxes like info@, support@, admin@, and sales@ so you can decide whether they belong in the campaign.",                                    col: "lg:col-span-4", tag: "04" },
    { t: "No-reply",        d: "Separates noreply and bounce-style addresses that are unlikely to become useful conversations.",                                                            col: "lg:col-span-4", tag: "05" },
    { t: "Disposable",      d: "Catches throwaway providers from a maintained disposable-domain list before they pollute your list quality.",                                                col: "lg:col-span-4", tag: "06" },
    { t: "Typo suggestions", d: "Surfaces likely provider typos like gmial.com and lets you approve fixes instead of blindly deleting potentially real contacts.",                         col: "lg:col-span-4", tag: "07" },
    { t: "Risky TLD",       d: "Flags top-level domains with high abuse or typo risk so they are visible in the audit trail before export.",                                                col: "lg:col-span-4", tag: "08" },
    { t: "Catch-all",       d: "Marks domains that accept unknown mailboxes as Needs review, so uncertain addresses do not masquerade as fully confirmed.",                                  col: "lg:col-span-4", tag: "09" },
  ]
  return (
    <section id="checks" className="relative" style={{ background: T.bgSoft }}>
      <ApertureStyles />
      <div className={`${MAXW} mx-auto px-6 lg:px-10 py-12 sm:py-16 md:py-24`}>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionEyebrow>What you get</SectionEyebrow>
            <h2
              className="mt-3 leading-[1.0] tracking-[-0.035em] max-w-3xl"
              style={{ fontSize: "clamp(2rem, 4vw, 3.1rem)", fontWeight: 800 }}
            >
              Nine checks. Clear reasons.{" "}
              <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>Fewer bad sends.</em>
            </h2>
          </div>
          <a
            href="/docs#checks"
            className="inline-flex items-center gap-1.5"
            style={{ fontFamily: FM, fontSize: 11.5, letterSpacing: "0.14em", textTransform: "uppercase", color: T.acc, fontWeight: 700 }}
          >
            Read the spec <ArrowR />
          </a>
        </div>

        <RevealGrid className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-4">
          {items.map((it, i) => (
            <div
              key={it.t}
              data-reveal
              className={`relative rounded-2xl p-6 col-span-12 ${it.col} group apl-card-hover overflow-hidden`}
              style={{
                background: T.surface,
                border: `1px solid ${T.hair}`,
                boxShadow: `0 1px 0 rgba(255,255,255,0.6) inset`,
                minHeight: 200,
                animationDelay: `${i * 70}ms`,
              }}
            >
              <div
                aria-hidden
                className="absolute -top-12 -right-12 w-56 h-56 rounded-full pointer-events-none transition-all duration-500 opacity-0 group-hover:opacity-100"
                style={{ background: T.accSoft, filter: "blur(60px)" }}
              />
              <div
                aria-hidden
                className="absolute inset-y-0 -left-1/2 w-1/2 pointer-events-none opacity-0 group-hover:opacity-100"
                style={{
                  background: `linear-gradient(90deg, transparent, ${T.accGlow}, transparent)`,
                  animation: "apl-shimmer-x 1.6s ease-in-out infinite",
                  filter: "blur(8px)",
                }}
              />
              <div className="relative flex items-center justify-between">
                <span style={{ fontFamily: FM, fontSize: 10.5, letterSpacing: "0.16em", color: T.ink3 }}>
                  /{it.tag}
                </span>
                <ValidatorIcon name={it.t} />
              </div>
              <h3 className="relative mt-5 tracking-[-0.015em]" style={{ fontSize: 17, fontWeight: 700 }}>
                {it.t}
              </h3>
              <p className="relative mt-2" style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.55 }}>{it.d}</p>
            </div>
          ))}
        </RevealGrid>
      </div>
    </section>
  )
}

function ValidatorIcon({ name }: { name: string }) {
  const stroke = T.acc
  const common = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none" } as const
  const sw = 1.6
  switch (name) {
    case "Syntax":
      return <svg {...common}><path d="M8 5l-4 7 4 7M16 5l4 7-4 7" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" /></svg>
    case "Live MX":
      return <svg {...common}><circle cx="12" cy="12" r="2" fill={stroke} /><circle cx="12" cy="12" r="6" stroke={stroke} strokeWidth={sw} /><circle cx="12" cy="12" r="10" stroke={stroke} strokeWidth={sw} strokeDasharray="2 3" /></svg>
    case "Duplicates":
      return <svg {...common}><path d="M7 7h10v10H7z" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" /><path d="M4 4h10v10" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" /></svg>
    case "Disposable":
      return <svg {...common}><path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" /></svg>
    case "Role-based":
      return <svg {...common}><circle cx="12" cy="9" r="3.2" stroke={stroke} strokeWidth={sw} /><path d="M5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5" stroke={stroke} strokeWidth={sw} strokeLinecap="round" /></svg>
    case "No-reply":
      return <svg {...common}><path d="M4 6h16v12H4z" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" /><path d="M4 7l8 6 8-6M6 18L18 6" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" /></svg>
    case "Typo suggestions":
      return <svg {...common}><path d="M4 18l5-12 5 12M6 13h6M16 6v8M16 18v.01" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" /></svg>
    case "Risky TLD":
      return <svg {...common}><path d="M12 3l8 4-8 4-8-4 8-4z" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" /><path d="M4 11l8 4 8-4M4 15l8 4 8-4" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" /></svg>
    case "Catch-all":
      return <svg {...common}><path d="M4 7h16v10H4z" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" /><path d="M4 8l8 6 8-6" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" /><path d="M8 20h8" stroke={stroke} strokeWidth={sw} strokeLinecap="round" /></svg>
    default:
      return null
  }
}
